import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { FC, useMemo } from "react";
import { useFormikContext } from "formik";
import classNames from "classnames";
import { Tag } from "@design-system/Tag/Tag";
import { ClientPricing } from "@hooks/billing/useClientPricing";
import { discountLabel } from "@helpers/billing";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { ClientFormSchema } from "./lib";
import {
    buildPlanStepOptions,
    getAutoSelectedPlan,
    isOrganizationOwnedPricingBlocked,
} from "./planHelpers";
import { OrganizationOwnedPricingStatus } from "./OrganizationOwnedPricingStatus";
import { V3BillingPreview } from "./V3BillingPreview";
import styles from "./PlanStep.module.scss";

type BillingOwnershipValue = "true" | "false";

interface PlanStepProps {
    clientPricing: ClientPricing;
}

export const PlanStep: FC<PlanStepProps> = ({ clientPricing }) => {
    const { values, setFieldValue, handleBlur } =
        useFormikContext<ClientFormSchema>();
    const activeOrganization = useActiveOrganization();
    const allowedPlans = activeOrganization?.allowedPlans;
    const organizationBillingTag = clientPricing.percentOffDiscount ? (
        <aside className={styles.addon}>
            <Tag color="accent" outline>
                {discountLabel(clientPricing.percentOffDiscount)}
            </Tag>
        </aside>
    ) : null;

    const options = useMemo(
        () =>
            buildPlanStepOptions({
                clientPricing,
                isOrgOwned: values.plan.billingOwnership,
                allowedPlans,
            }),
        [clientPricing, values.plan.billingOwnership, allowedPlans],
    );

    const isPricingBlocked = isOrganizationOwnedPricingBlocked({
        clientPricing,
        isOrgOwned: values.plan.billingOwnership,
    });

    const handleBillingOwnershipChange = (
        value: BillingOwnershipValue,
    ): void => {
        const newIsOrgOwned = value === "true";
        setFieldValue("plan.billingOwnership", newIsOrgOwned);
        handleBlur("plan.billingOwnership");

        const autoSelectedPlan = getAutoSelectedPlan(
            newIsOrgOwned,
            values.plan.plan,
            allowedPlans,
        );
        if (autoSelectedPlan) {
            setFieldValue("plan.plan", autoSelectedPlan);
            handleBlur("plan.plan");
        }
    };

    const handlePlanChange = (value: FLAT_RATE_PLANS): void => {
        if (
            values.isMultiEntity === true &&
            value !== FLAT_RATE_PLANS.PLUS &&
            value !== FLAT_RATE_PLANS.ADVANCED
        ) {
            return;
        }

        setFieldValue("plan.plan", value);
        handleBlur("plan.plan");
    };

    return (
        <div>
            <section className={styles.section}>
                <header className={styles.header}>
                    Who is paying for the subscription?
                </header>
                <RadioGroup<BillingOwnershipValue>
                    name="billingOwnership"
                    aria-label="Subscription payer"
                    value={values.plan.billingOwnership ? "true" : "false"}
                    onValueChange={handleBillingOwnershipChange}
                    className={classNames(styles.radioGroup, styles.horizontal)}
                >
                    <Radio
                        className={styles.radio}
                        label={
                            <>
                                Bill my firm
                                {organizationBillingTag}
                            </>
                        }
                        value="true"
                    />

                    <Radio
                        className={styles.radio}
                        label={<>Bill client</>}
                        value="false"
                    />
                </RadioGroup>
            </section>

            <section className={styles.section}>
                <header className={styles.header}>Select plan</header>
                {isPricingBlocked ? (
                    <OrganizationOwnedPricingStatus
                        clientPricing={clientPricing}
                        isOrgOwned={values.plan.billingOwnership}
                    />
                ) : (
                    <RadioGroup<FLAT_RATE_PLANS>
                        name="plan"
                        aria-label="Plan"
                        value={values.plan.plan}
                        onValueChange={handlePlanChange}
                        className={styles.radioGroup}
                    >
                        {options.map((option) => {
                            const isPlusLocked =
                                values.isMultiEntity === true &&
                                option.value !== FLAT_RATE_PLANS.PLUS &&
                                option.value !== FLAT_RATE_PLANS.ADVANCED;

                            return (
                                <Radio
                                    className={styles.radio}
                                    key={option.value}
                                    disabled={isPlusLocked}
                                    label={
                                        <>
                                            <div className={styles.main}>
                                                {option.label}
                                                {option.isComplimentary && (
                                                    <Tag color="neutral">
                                                        Complimentary
                                                    </Tag>
                                                )}
                                                <small
                                                    className={
                                                        styles.description
                                                    }
                                                >
                                                    {option.description}
                                                </small>
                                                {values.isMultiEntity ===
                                                    true &&
                                                    option.value ===
                                                        FLAT_RATE_PLANS.PLUS && (
                                                        <small
                                                            className={
                                                                styles.description
                                                            }
                                                        >
                                                            Required for
                                                            multi-entity
                                                        </small>
                                                    )}
                                            </div>
                                            <aside className={styles.addon}>
                                                {option.addon}
                                            </aside>
                                        </>
                                    }
                                    value={option.value}
                                />
                            );
                        })}
                    </RadioGroup>
                )}
                <V3BillingPreview
                    clientPricing={clientPricing}
                    plan={values.plan.plan}
                    isOrgOwned={values.plan.billingOwnership}
                />
            </section>
        </div>
    );
};
